// Phase 8 R2 — atomically replace all lines for a canned job.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface CannedJobLineInput {
  line_type: 'part' | 'labor';
  part_description?: string | null;
  part_number?: string | null;
  quantity?: number | null;
  labor_description?: string | null;
  labor_hours?: number | null;
  /** CAT-1/CAT-4: optional canonical jobs_catalog.code for a labor line. */
  catalog_job_code?: string | null;
}

interface Vars {
  cannedJobId: string;
  lines: CannedJobLineInput[];
}

export function useReplaceCannedJobLines() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ cannedJobId, lines }: Vars): Promise<number> => {
      const { data, error } = await (supabase as any).rpc('replace_canned_job_lines', {
        p_canned_job_id: cannedJobId,
        p_lines: lines as any,
      });
      if (error) throw error;
      return Number(data?.line_count ?? 0);
    },
    onSuccess: (count, { cannedJobId }) => {
      qc.invalidateQueries({ queryKey: ['canned-job-lines', cannedJobId] });
      qc.invalidateQueries({ queryKey: ['canned-jobs-admin'] });
      qc.invalidateQueries({ queryKey: ['canned-jobs'] });
      toast({
        title: 'Lines saved',
        description: `${count} line${count === 1 ? '' : 's'} saved.`,
      });
    },
    onError: (err: any) => {
      toast({
        title: 'Could not save lines',
        description: err?.message ?? 'Unknown error',
        variant: 'destructive',
      });
    },
  });
}
